import { Link } from "react-router-dom";
import { AlertTriangle, Zap } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { usePlan } from "../../hooks/usePlan";
import { useUsage } from "../../hooks/useUsage";

// Fallback if the usage API hasn't resolved yet (mirrors db.PLAN_LIMITS free).
const FALLBACK_FREE_LIMIT = 50;
const WARN_RATIO = 0.8; // start nudging in the last 20% of the monthly allotment

/**
 * AccountBanners — lightweight, dismissible-free conversion + dunning signals
 * shown at the top of the authed app:
 *   1. Past-due payment (retention / involuntary churn recovery)
 *   2. Free-tier usage warning as the monthly link limit approaches (conversion)
 */
export default function AccountBanners() {
  const { user } = useAuth();
  const { plan, isAdmin } = usePlan();
  const { usage, limits } = useUsage();
  const isFree = !isAdmin && plan === "free";

  if (!user) return null;

  const pastDue = user.subscription_status === "past_due";
  // API-driven limit (null = unlimited); fall back to the legacy field/const
  // before the usage call resolves so the banner still works on first paint.
  const limit = limits?.links_per_month ?? FALLBACK_FREE_LIMIT;
  const used = usage?.links ?? user.links_created_this_month ?? 0;
  const hasFiniteLimit = isFree && typeof limit === "number";
  const warnAt = hasFiniteLimit ? Math.ceil(limit * WARN_RATIO) : Infinity;
  const showUsage = hasFiniteLimit && used >= warnAt;
  const atLimit = hasFiniteLimit && used >= limit;

  if (!pastDue && !showUsage) return null;

  return (
    <div className="flex flex-col gap-2 px-4 pt-4 md:px-8">
      {pastDue && (
        <div
          className="flex items-center gap-3 rounded-2xl border-2 border-ink bg-pink/30 px-4 py-3 shadow-sticker-xs"
          data-testid="banner-past-due"
        >
          <AlertTriangle size={18} strokeWidth={2.5} className="shrink-0 text-ink" />
          <p className="flex-1 font-sans text-sm font-semibold text-ink">
            Your last payment failed. Update your billing to keep Pro features active.
          </p>
          <Link
            to="/billing"
            className="candy-press shrink-0 rounded-full border-2 border-ink bg-white px-3 py-1.5 font-display text-xs font-bold text-ink"
          >
            Update billing
          </Link>
        </div>
      )}

      {showUsage && (
        <div
          className="flex items-center gap-3 rounded-2xl border-2 border-ink bg-yellow-tint px-4 py-3 shadow-sticker-xs"
          data-testid="banner-usage"
        >
          <Zap size={18} strokeWidth={2.5} className="shrink-0 text-ink" />
          <p className="flex-1 font-sans text-sm font-semibold text-ink">
            {atLimit
              ? `You've hit your ${limit} free links this month. Upgrade to Pro for more.`
              : `${used} of ${limit} free links used this month — ${limit - used} left.`}
          </p>
          <Link
            to="/pricing"
            className="candy-press shrink-0 rounded-full border-2 border-ink bg-candy px-3 py-1.5 font-display text-xs font-bold text-white"
          >
            Upgrade
          </Link>
        </div>
      )}
    </div>
  );
}
